import React from 'react';
import { Grid, Box, makeStyles } from '@material-ui/core';
import {
  RadioButtonGroup,
  FormValueTypes,
  FormHookResult,
  DisplayItem,
  Body2Text,
  colors,
} from 'design-system';
import { MessageBoxSpace } from 'src/components/general';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles({
  title: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: '25px',
    color: colors.madison,
  },
  buttonEdit: {
    cursor: 'pointer',
    width: 95,
  },
  edit: {
    color: colors.bright_turquoise,
    fontSize: 16,
    fontWeight: 700,
    marginLeft: '10px',
  },
  subTitle: {
    marginTop: '10px',
  },
  alertBox: {
    marginTop: '10px',
  },
});

const booleanRadioData: DisplayItem[] = [
  { Value: 'yes', Text: 'Sí, agregar beneficiarios' },
  { Value: 'no', Text: 'Asignar beneficiarios en otro momento' },
];

function getMappedBooleanValue(value: FormValueTypes): string | undefined {
  if (typeof value !== 'undefined') {
    return value ? 'yes' : 'no';
  }
  return value;
}

type Props = {
  showSaveAlert: boolean;
  getInputProps: FormHookResult;
  onSetBeneficiaries: () => void;
  onSetLegalHeirs: () => void;
  onCloseSaveAlert: () => void;
};

const LegalHeirsCards = ({
  showSaveAlert,
  getInputProps,
  onSetBeneficiaries,
  onSetLegalHeirs,
  onCloseSaveAlert,
}: Props): JSX.Element => {
  const classes = useStyles();
  const { value, onChange, errorText } = getInputProps('enableInheritor');
  const enableInheritor = getMappedBooleanValue(value);
  const legalHeirsSelected = enableInheritor === 'no';

  const handleChangeOption = (optionYes: boolean): void => {
    onChange(optionYes);
    if (optionYes) {
      onSetBeneficiaries();
    } else {
      onSetLegalHeirs();
    }
  };
  return (
    <Grid data-testid="legal-heirs-cards">
      <Grid container alignItems="center">
        <Grid item xs={12}>
          <Box display="flex" sx={{ width: '100%' }}>
            <Box
              sx={{ width: `calc(100% - ${legalHeirsSelected ? 0 : 95}px)` }}
            >
              <span className={classes.title}>
                Esta póliza cuenta con seguro de vida accidental
              </span>
            </Box>
            {!legalHeirsSelected && (
              <Box
                id="swi-edit-beneficiary-icon"
                data-testid="edit-beneficiary-icon"
                className={classes.buttonEdit}
                onClick={onSetBeneficiaries}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  fontSize={24}
                  color={colors.bright_turquoise}
                />
                <span className={classes.edit}>EDITAR</span>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} className={classes.subTitle}>
          <Body2Text>¿Deseas añadir beneficiarios?</Body2Text>
        </Grid>

        <Grid item xs={12}>
          <RadioButtonGroup
            data-testid="swi-radio-Inheritor"
            inputId="swi-radio-Inheritor"
            radioGroupName="Inheritor"
            formTitle=""
            defaultValue={enableInheritor}
            radioData={booleanRadioData}
            onChange={(newVal): void => {
              handleChangeOption(newVal.Value === 'yes');
            }}
            errorMessage={errorText}
            centerLabels={false}
            formControlStyle={{ maxWidth: '100%' }}
            gridSize={12}
          />
        </Grid>

        {legalHeirsSelected && (
          <Grid item xs={12} className={classes.alertBox}>
            <MessageBoxSpace
              data-testid="info-alert"
              message="Se asignarán herederos legales como beneficiarios. Una vez emitida la póliza, se pueden cambiar o añadir los beneficiarios."
              variant="info"
              hideCloseButton
            />
          </Grid>
        )}

        {showSaveAlert && (
          <Grid item xs={12} className={classes.alertBox}>
            <MessageBoxSpace
              message="Los beneficiarios han sido añadidos y guardados."
              variant="success"
              data-testid="save-success-alert"
              onCloseClick={onCloseSaveAlert}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default LegalHeirsCards;
